import * as THREE from 'three';
import type { GUI } from 'lil-gui';
import { stripMayaNamespace } from '../utils/mayaNamespace.js';
import { applyRoleToMaterial, type RoleGroupsJson } from '../materials/applyRoleOverrides.js';
import type { RoleDef } from '../materials/roleDefs.js';

/**
 * RAV4 ↔ LX role 페어링 (dev-time, ?pair=1).
 *   - LX 부품 클릭 → 그 mesh 의 userData.role 을 브러시로 집음
 *   - RAV4 부품 클릭 → 브러시 role 적용 (mat 모드: baseline / mesh 모드: overrides[])
 *   - 적용 즉시 화면 반영 + BoxHelper 하이라이트
 *   - save → /api/material-groups (dev plugin) 로 rav4.json 디스크 write
 */

export interface RolePairingDeps {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  domElement: HTMLElement;
  /** role 태깅된 LX (mesh.userData.role 보유) */
  lxGroup: THREE.Object3D;
  /** 타겟 RAV4 */
  ravGroup: THREE.Object3D;
  roleDefs: Record<string, RoleDef>;
  /** 현재 RAV4 매핑 (in-memory, 변경됨). save 시 그대로 직렬화 */
  ravJson: RoleGroupsJson;
  /** LX baseline (이름일치 일괄적용용) */
  lxBaseline: Record<string, string>;
  gui: GUI;
  invalidate: () => void;
}

function matNameOf(mesh: THREE.Mesh): string {
  const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  return m ? stripMayaNamespace(m.name) : '';
}

function roleOf(mesh: THREE.Mesh): string | undefined {
  const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
  return m?.userData?.role as string | undefined;
}

export function createRolePairing(deps: RolePairingDeps): void {
  const { scene, camera, domElement, lxGroup, ravGroup, roleDefs, ravJson, lxBaseline, gui, invalidate } = deps;

  const state = { mode: 'pair' as 'pair' | 'select', unit: 'mat' as 'mat' | 'mesh', brush: '(none)', selected: '(none)' };
  let brushRole: string | null = null;
  let helper: THREE.BoxHelper | null = null;
  // 각 적용(단일/일괄)의 역연산 스택. undo 시 pop 후 실행 — JSON + 머티리얼 동시 복원.
  const undoStack: Array<() => void> = [];

  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  // --- lil-gui ---
  const folder = gui.addFolder('Role Pairing');
  folder.add(state, 'mode', ['pair', 'select']).name('모드 (pair/select)');
  folder.add(state, 'unit', ['mat', 'mesh']).name('단위');
  const brushCtrl = folder.add(state, 'brush').name('brush').disable();
  const selCtrl = folder.add(state, 'selected').name('선택').disable();
  folder.add({ fn: bulkCopyLxNames }, 'fn').name('LX 이름일치 일괄적용');
  folder.add({ fn: undo }, 'fn').name('undo (Ctrl+Z)');
  const saveCtrl = folder.add({ fn: saveJson }, 'fn').name('save → rav4.json');
  folder.open();

  // --- 피킹 ---
  function pick(ev: PointerEvent, target: THREE.Object3D): THREE.Mesh | null {
    const rect = domElement.getBoundingClientRect();
    ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    for (const h of raycaster.intersectObject(target, true)) {
      if (h.object instanceof THREE.Mesh) return h.object;
    }
    return null;
  }

  function highlight(mesh: THREE.Mesh): void {
    if (helper) scene.remove(helper);
    helper = new THREE.BoxHelper(mesh, 0xff3333);
    scene.add(helper);
    invalidate();
  }

  function pickBrush(mesh: THREE.Mesh): void {
    const role = roleOf(mesh);
    if (!role) { console.warn('[pairing] LX mesh 에 role 없음:', mesh.name); return; }
    brushRole = role;
    state.brush = role;
    brushCtrl.updateDisplay();
    highlight(mesh);
    console.log(`[pairing] brush = ${role} (LX ${mesh.name})`);
  }

  // select 모드 — 적용 없이 선택 + mat/role 표시 (살펴보기)
  function selectInspect(mesh: THREE.Mesh): void {
    const matName = matNameOf(mesh);
    const role = roleOf(mesh) ?? ravJson.baseline[matName] ?? '(none)';
    highlight(mesh);
    state.selected = `${matName} → ${role}`;
    selCtrl.updateDisplay();
    console.log(`[pairing] 선택: mesh=${mesh.name} mat=${matName} role=${role}`);
  }

  function applyToMatName(matName: string, role: string): void {
    ravGroup.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const isArr = Array.isArray(o.material);
      const mats: THREE.Material[] = isArr ? (o.material as THREE.Material[]) : [o.material as THREE.Material];
      let changed = false;
      mats.forEach((m, i) => {
        if (m && stripMayaNamespace(m.name) === matName) {
          const next = applyRoleToMaterial(m, role, roleDefs);
          if (next !== m) { mats[i] = next; changed = true; }
        }
      });
      if (changed) o.material = isArr ? mats : mats[0]!;
    });
  }

  function applyToMesh(mesh: THREE.Mesh, slot: number, role: string): void {
    const isArr = Array.isArray(mesh.material);
    const mats: THREE.Material[] = isArr ? (mesh.material as THREE.Material[]) : [mesh.material as THREE.Material];
    const m = mats[slot];
    if (!m) return;
    const next = applyRoleToMaterial(m, role, roleDefs);
    if (next !== m) { mats[slot] = next; mesh.material = isArr ? mats : mats[0]!; }
  }

  function upsertOverride(mesh: string, slot: number, role: string, baseMat: string): void {
    ravJson.overrides ??= [];
    const hit = ravJson.overrides.find((o) => o.mesh === mesh && o.slot === slot);
    if (hit) { hit.role = role; return; }
    ravJson.overrides.push({
      mesh, slot, role,
      _baseline_mat: baseMat,
      _baseline_role: ravJson.baseline[baseMat] ?? '?',
    });
  }

  function applyBrush(mesh: THREE.Mesh): void {
    if (!brushRole) { console.warn('[pairing] 브러시 없음 — LX 부품 먼저 클릭'); return; }
    const role = brushRole;
    const matName = matNameOf(mesh);
    if (state.unit === 'mat') {
      const prevRole = ravJson.baseline[matName];
      ravJson.baseline[matName] = role;
      applyToMatName(matName, role);
      undoStack.push(() => {
        if (prevRole !== undefined) { ravJson.baseline[matName] = prevRole; applyToMatName(matName, prevRole); }
        else delete ravJson.baseline[matName];
      });
      console.log(`[pairing] mat ${matName} → ${role}`);
    } else {
      const prevRole = ravJson.overrides?.find((o) => o.mesh === mesh.name && o.slot === 0)?.role;
      upsertOverride(mesh.name, 0, role, matName);
      applyToMesh(mesh, 0, role);
      undoStack.push(() => {
        if (prevRole !== undefined) {
          upsertOverride(mesh.name, 0, prevRole, matName);
          applyToMesh(mesh, 0, prevRole);
        } else {
          ravJson.overrides = (ravJson.overrides ?? []).filter((o) => !(o.mesh === mesh.name && o.slot === 0));
          const baseRole = ravJson.baseline[matName];
          if (baseRole) applyToMesh(mesh, 0, baseRole);
        }
      });
      console.log(`[pairing] mesh ${mesh.name} → ${role}`);
    }
    highlight(mesh);
    invalidate();
  }

  function bulkCopyLxNames(): void {
    const changes: Array<[string, string]> = []; // [matName, prevRole]
    for (const matName of Object.keys(ravJson.baseline)) {
      const lxRole = lxBaseline[matName];
      if (lxRole && ravJson.baseline[matName] !== lxRole) {
        changes.push([matName, ravJson.baseline[matName]!]);
        ravJson.baseline[matName] = lxRole;
        applyToMatName(matName, lxRole);
      }
    }
    if (changes.length) {
      undoStack.push(() => {
        for (const [matName, prevRole] of changes) {
          ravJson.baseline[matName] = prevRole;
          applyToMatName(matName, prevRole);
        }
      });
    }
    console.log(`[pairing] LX 이름일치 일괄적용: ${changes.length} mats 변경`);
    invalidate();
  }

  function undo(): void {
    const fn = undoStack.pop();
    if (!fn) { console.log('[pairing] undo: 되돌릴 작업 없음'); return; }
    fn();
    invalidate();
    console.log(`[pairing] undo 완료 (남은 ${undoStack.length})`);
  }

  async function saveJson(): Promise<void> {
    try {
      const res = await fetch('/api/material-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: 'rav4', json: ravJson }),
      });
      if (res.ok) {
        console.log('[pairing] saved → public/data/material-groups/rav4.json');
        saveCtrl.name('saved ✓');
        setTimeout(() => saveCtrl.name('save → rav4.json'), 1500);
      } else {
        console.error('[pairing] save 실패:', res.status, await res.text());
        saveCtrl.name('save 실패!');
      }
    } catch (e) {
      console.error('[pairing] save 에러:', e);
      saveCtrl.name('save 에러!');
    }
  }

  // --- 클릭 vs 드래그 구분 (camera-controls orbit 과 공존) ---
  let downX = 0, downY = 0;
  domElement.addEventListener('pointerdown', (e) => { downX = e.clientX; downY = e.clientY; });
  domElement.addEventListener('pointerup', (e) => {
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > 5) return; // 드래그 → 무시
    if (state.mode === 'select') {
      // 살펴보기 — 적용 없이 선택만 (LX/RAV 공통)
      const hit = pick(e, lxGroup) ?? pick(e, ravGroup);
      if (hit) selectInspect(hit);
      return;
    }
    const lxHit = pick(e, lxGroup);
    if (lxHit) { pickBrush(lxHit); return; }
    const ravHit = pick(e, ravGroup);
    if (ravHit) applyBrush(ravHit);
  });

  // Ctrl/Cmd+Z → undo
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      undo();
    }
  });

  console.log('[pairing] 활성 — LX(왼쪽) 부품 클릭=role 집기, RAV4(오른쪽) 부품 클릭=적용, Ctrl+Z=undo');
}
